import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppIcon from '../../../../components/AppIcon';
import type { Goal } from '../types';

interface GoalNodeProps {
  goal: Goal;
  level?: Goal['level'];
  onNodeClick?: (goal: Goal) => void;
  isConnected?: boolean;
}

const GoalNode: React.FC<GoalNodeProps> = ({
  goal,
  level = 'individual',
  onNodeClick = () => {},
  isConnected = false,
}) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const getLevelStyles = () => {
    switch (level) {
      case 'company':
        return {
          bg: 'linear-gradient(135deg, var(--color-primary) 0%, #8b5cf6 100%)',
          textColor: 'text-white',
          isWhiteText: true,
          width: 360,
          height: 120,
          fontSize: 'text-lg',
          fontWeight: 'font-semibold',
          shadow: 'shadow-lg',
        };
      case 'team':
        return {
          bg: 'linear-gradient(135deg, #f59e0b 0%, #eab308 100%)',
          textColor: 'text-white',
          isWhiteText: true,
          width: 320,
          height: 100,
          fontSize: 'text-base',
          fontWeight: 'font-medium',
          shadow: 'shadow-md',
        };
      case 'individual':
      default:
        return {
          bg: 'var(--color-card)',
          textColor: 'text-foreground',
          isWhiteText: false,
          width: 280,
          height: 88,
          fontSize: 'text-sm',
          fontWeight: 'font-medium',
          shadow: 'shadow-sm',
        };
    }
  };

  const getStatusIcon = () => {
    switch (goal.status) {
      case 'completed': return 'CheckCircle';
      case 'on-track': return 'Target';
      case 'at-risk': return 'AlertTriangle';
      case 'overdue': return 'XCircle';
      default: return 'Circle';
    }
  };

  const getStatusColor = () => {
    switch (goal.status) {
      case 'completed': return 'var(--color-success)';
      case 'on-track': return 'var(--color-primary)';
      case 'at-risk': return 'var(--color-warning)';
      case 'overdue': return 'var(--color-error)';
      default: return 'var(--color-muted-foreground)';
    }
  };

  const styles = getLevelStyles();

  return (
    <div className="relative" data-testid={`goal-node-${goal.id}`}>
      <div
        className={`w-full rounded-lg p-4 cursor-pointer transition-all duration-200 overflow-hidden flex flex-col justify-between ${styles.shadow} ${
          level === 'individual' ? 'border-2 border-border' : ''
        } ${isConnected ? 'ring-2 ring-primary/50' : ''}`}
        style={{
          background: styles.bg,
          minHeight: styles.height,
          transform: isHovered ? 'scale(1.03)' : 'scale(1)',
          boxShadow: isHovered ? '0 8px 24px rgba(0, 0, 0, 0.12)' : undefined,
        }}
        onClick={() => onNodeClick(goal)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex justify-between items-start">
          <div className="flex-1 me-2 overflow-hidden">
            <p
              className={`${styles.fontSize} ${styles.fontWeight} truncate mb-0.5 ${styles.textColor}`}
            >
              {goal.title}
            </p>
            <div className="flex items-center gap-1">
              <AppIcon
                name={getStatusIcon()}
                size={14}
                color={styles.isWhiteText ? 'rgba(255, 255, 255, 0.9)' : getStatusColor()}
              />
              <span
                className="inline-flex items-center px-1 h-5 rounded text-[0.65rem] font-semibold uppercase"
                style={{
                  backgroundColor: styles.isWhiteText
                    ? 'rgba(255, 255, 255, 0.2)'
                    : `${getStatusColor()}18`,
                  color: styles.isWhiteText ? 'inherit' : getStatusColor(),
                }}
              >
                {goal.status.replace('-', ' ')}
              </span>
              {goal.milestones && goal.milestones.length > 0 && (
                <span
                  className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[0.6rem] font-semibold"
                  style={{
                    backgroundColor: styles.isWhiteText ? 'rgba(255, 255, 255, 0.2)' : 'color-mix(in srgb, var(--color-primary) 15%, transparent)',
                    color: styles.isWhiteText ? 'inherit' : 'var(--color-primary)',
                  }}
                  title={t('{{completed}}/{{total}} milestones completed', { completed: goal.milestones.filter(m => m.completed).length, total: goal.milestones.length })}
                  data-testid={`milestone-indicator-${goal.id}`}
                >
                  <AppIcon
                    name="Flag"
                    size={10}
                    color={styles.isWhiteText ? 'rgba(255, 255, 255, 0.9)' : 'var(--color-primary)'}
                  />
                  {goal.milestones.filter(m => m.completed).length}/{goal.milestones.length}
                </span>
              )}
              {goal.hasActiveBlockers && (
                <AppIcon
                  name="AlertOctagon"
                  size={14}
                  color="var(--color-error)"
                  title={t('{{count}} active blocker', { count: goal.activeBlockerCount || 0 })}
                />
              )}
            </div>
            {goal.owner && (
              <span
                className={`block text-[0.7rem] mt-0.5 truncate ${
                  styles.isWhiteText ? 'opacity-75' : 'text-muted-foreground opacity-75'
                }`}
              >
                {goal.owner}
              </span>
            )}
          </div>
          <AppIcon
            name="ChevronRight"
            size={16}
            color={styles.isWhiteText ? 'rgba(255, 255, 255, 0.6)' : 'var(--color-muted-foreground)'}
            className={isHovered ? 'translate-x-1' : ''}
          />
        </div>

        <div>
          <div className="flex justify-between mb-0.5">
            <span
              className={`text-[0.7rem] ${
                styles.isWhiteText ? 'opacity-80' : 'text-muted-foreground opacity-70'
              }`}
            >
              {goal.progress}%
            </span>
          </div>
          <div
            className="h-[5px] rounded-full overflow-hidden"
            style={{
              backgroundColor: styles.isWhiteText ? 'rgba(255,255,255,0.2)' : 'var(--color-muted)',
            }}
          >
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${goal.progress}%`,
                backgroundColor: styles.isWhiteText ? 'white' : getStatusColor(),
              }}
            />
          </div>
        </div>
      </div>

      {/* Hover Tooltip */}
      {isHovered && goal.description && (
        <div
          className="absolute z-[1010] top-full left-1/2 -translate-x-1/2 mt-2 w-80 bg-card border border-border rounded-lg shadow-[0_10px_40px_rgba(0,0,0,0.15)] p-4"
        >
          <h6 className="font-medium text-foreground mb-1">
            {goal.title}
          </h6>
          <p className="text-sm text-muted-foreground mb-2">
            {goal.description}
          </p>
          {goal.milestones && goal.milestones.length > 0 && (
            <div>
              <span className="text-xs font-medium text-foreground mb-1 block">
                {t('Key Milestones:')}
              </span>
              <ul className="list-none p-0 m-0">
                {goal.milestones.slice(0, 3).map((milestone, index) => (
                  <li key={index} className="flex items-center gap-1 mb-0.5">
                    <AppIcon
                      name={milestone.completed ? "CheckCircle" : "Circle"}
                      size={12}
                      color={milestone.completed ? "var(--color-success)" : "var(--color-muted-foreground)"}
                    />
                    <span className="text-xs text-muted-foreground">
                      {milestone.title}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GoalNode;
